import React, { useState } from "react";
import {
  DatePicker,
  Button,
  List,
  Typography,
  Flex,
  theme,
  Popover,
} from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";

import { util } from "../../utils/functions/custom";

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { useToken } = theme;

const financialYearPresets = util.getLastFinancialYears(3).map((fy) => ({
  key: fy.label,
  label: fy.isCurrentFY ? `Current FinYear ${fy.label}` : `Last FinYear ${fy.label}`,
  start: fy.start,
  end: fy.end,
  isCurrentFY: fy.isCurrentFY,
}));

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "3m", label: "Last 3 months" },
  { key: "6m", label: "Last 6 months" },
  { key: "9m", label: "Last 9 months" },
  // { key: "2025/26", label: "2025/26 FinYear" },
  // { key: "2024/25", label: "2024/25 FinYear" },
  // { key: "2023/24", label: "2023/24 FinYear" },
  ...financialYearPresets,
];

const currentFinYear = PRESETS.find((p) => p.isCurrentFY);

export const CustomDateRangePicker = ({ onApply }) => {
  const { token } = useToken();

  const getInitialRange = () => {
    if (currentFinYear) {
      return [currentFinYear.start, currentFinYear.end];
    }
    return [
      dayjs().subtract(7, "day").startOf("day"),
      dayjs().endOf("day"),
    ];
  };

  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(currentFinYear?.key || "custom");
  
  const [range, setRange] = useState(getInitialRange());

  const applyPreset = (key) => {
    setSelectedKey(key);
    const today = dayjs();

    let newStart = today.startOf("day");
    let newEnd = today.endOf("day");

    const dynamic = PRESETS.find((p) => p.key === key && p.start && p.end);

    if (dynamic) {
      setRange([dynamic.start, dynamic.end]);
      return;
    }

    if (key === "today") {
    } else if (key === "yesterday") {
      newStart = today.subtract(1, "day").startOf("day");
      // newEnd = today.subtract(1, "day").endOf("day");
      newEnd = today;
    } else if (key === "7d") {
      newStart = today.subtract(7, "day").startOf("day");
    } else if (key === "3m") {
      newStart = today.subtract(3, "month").startOf("day");
    } else if (key === "6m") {
      newStart = today.subtract(6, "month").startOf("day");
    } else if (key === "9m") {
      newStart = today.subtract(9, "month").startOf("day");
    } else if (key === "12m") {
      newStart = today.subtract(12, "month").startOf("day");
    }

    setRange([newStart, newEnd]);
  };

  const handleRangeChange = (dates) => {
    if (!dates) return;

    setSelectedKey("custom");

    let [start, end] = dates;

    if (start && end) {
      start = start.startOf("day");
      end = end.endOf("day");

      if (start.isAfter(end)) {
        end = start.endOf("day");
      }

      setRange([start, end]);
    }
  };

  const handleSelectedRange = () => {
    onApply &&
      onApply(
        range[0],
        range[1],
        selectedKey === "custom" ? undefined : selectedKey,
      );

    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const dateFormat = "YYYY/MM/DD";

  const content = (
    <Flex
      style={{
        width: 650,
        background: token.colorBgContainer,
        borderRadius: 8,
      }}
    >
      {/* LEFT - Presets */}
      <div
        style={{
          width: 220,
          borderRight: `1px solid ${token.colorBorder}`,
          padding: "12px 0",
        }}
      >
        <div style={{ padding: "0 16px 8px", fontWeight: "bold" }}>
          Date Filters
        </div>

        <List
          dataSource={PRESETS}
          renderItem={(item) => (
            <List.Item
              onClick={() => applyPreset(item.key)}
              style={{
                padding: "8px 16px",
                cursor: "pointer",
                background:
                  selectedKey === item.key || item.isCurrentFY
                    ? token.colorPrimaryBg
                    : "transparent",

          borderLeft: selectedKey === item.key || item.isCurrentFY
            ? `3px solid ${token.colorPrimary}`
            : "3px solid transparent",
              }}
            >
              <Text
          strong={item.isCurrentFY}
          style={{
            color: selectedKey === item.key
              ? token.colorPrimary
              : item.isCurrentFY
              ? token.colorText
              : token.colorText,
          }}
        >
          {item.label}
        </Text>
            </List.Item>
          )}
          split={false}
        />
      </div>

      {/* RIGHT */}
      <div style={{ flex: 1, padding: "16px" }}>
        <Text strong>Select date range</Text>

        <div style={{ marginTop: 12 }}>
          <RangePicker
            value={range}
            onChange={handleRangeChange}
            format={dateFormat}
            style={{ width: "100%" }}
            allowClear={true}
          />
        </div>

        <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
          <Button size="small" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="small" type="primary" onClick={handleSelectedRange}>
            Confirm
          </Button>
        </Flex>
      </div>
    </Flex>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      {/* Trigger Control */}
      <Button className="date-range">
        {range[0].format(dateFormat)} → {range[1].format(dateFormat)}
      </Button>
    </Popover>
  );
};

// export default CustomDateRangePicker;
